/**
 * Expandable artifact card. Collapsed shows the metadata strip
 * (tool · name · size). Expanded fetches the full content via
 * GET /api/artifacts/{id} and renders it through SkillResultMarkdown
 * so JSON / SOQL / apex bodies get Monaco code-block treatment with
 * copy buttons — same component the dev-agent chat uses for tool output.
 */
import { DiffEditor, Editor, type Monaco } from "@monaco-editor/react";
import {
  ChevronDown, ChevronRight, Copy, Loader2, Package, X,
  FileText, FileAudio, Image as ImageIcon, Link2, GitCompare, Code2, Plus, Maximize2,
  ExternalLink, ShieldCheck,
} from "lucide-react";
import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FlowDiagramView, looksLikeFlow } from "./FlowDiagramView";
import { useTheme } from "next-themes";
import { registerAppMonacoThemes } from "@/lib/studio/monacoTheme";
import { SkillResultMarkdown } from "@/components/organisms/studio/SkillResultMarkdown";
import { getArtifact, humanizeStatus, startLwcPreview, validateApex, type ArtifactContent, type ApexValidateResult } from "@/services/multi-agent";
import { useMultiAgentStore } from "@/stores/multiAgentStore";
import { Button } from "@/components/atoms/multi-agent/Button";
import { sanitizeArtifactLabel } from "@/services/multi-agent/artifactDisplay";
import { dataColumns, displayCell, pickDataRows, readDataCell } from "@/services/multi-agent/dataRows";
import { bareClassName } from "@/pages/multi-agent/ide/utils/bareClassName";
import { useCopyToClipboard } from "@/hooks/multi-agent/useCopyToClipboard";
import { useArtifactOverlayStore } from "@/stores/multiAgentArtifactOverlayStore";
import { formatBytes } from "@/lib/studio/utils";
import {
  extractApexName,
  inferMetadataType,
  openGeneratedCodeInMultiAgentWorkspace,
} from "@/pages/multi-agent/ide/codeActions";
import { VALIDATION_COLOR, RISK_COLOR } from "@/constants/multi-agent/artifact.constants";

// Guess a fence language from the artifact's tool / type / name so the
// markdown code block highlights correctly.
function fenceLang(a: ArtifactProps["artifact"], fetched: ArtifactContent | null): string {
  if (fetched?.language) return fetched.language;
  const t = (a.type || "").toLowerCase();
  if (t.includes("apex")) return "apex";
  if (t.includes("soql") || t.includes("sql")) return "sql";
  if (t.includes("xml")) return "xml";
  if (t.includes("yaml") || t.includes("yml")) return "yaml";
  if (t.includes("md") || t.includes("markdown")) return "markdown";
  const name = (a.name || "").toLowerCase();
  if (name.endsWith(".cls") || name.endsWith(".trigger")) return "apex";
  if (name.endsWith(".xml")) return "xml";
  if (name.endsWith(".sql")) return "sql";
  return "json";
}

// If the artifact content is plain JSON, pretty-print it so the rendered
// code block is readable rather than a single 4kb line.
function maybePretty(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) return raw;
  try {
    return JSON.stringify(JSON.parse(trimmed), null, 2);
  } catch {
    return raw;
  }
}

/** Icon for an artifact type — uploaded intake kinds + code patches get a
 *  meaningful glyph instead of the generic package. */
function iconForArtifactType(type: string) {
  switch (type) {
    case "intake_pdf": return FileText;
    case "intake_image": return ImageIcon;
    case "intake_audio": return FileAudio;
    case "intake_url": return Link2;
    case "intake_text": return FileText;
    case "code_patch":
    case "diff": return GitCompare;
    case "apex_class":
    case "apex_trigger": return Code2;
    default: return Package;
  }
}

/** Concise typed-metadata label for an uploaded attachment (tool_name='intake').
 *  null for non-intake artifacts (renders nothing). */
function intakeChip(intake?: Record<string, unknown> | null): string | null {
  if (!intake) return null;
  const kind = String(intake.source_kind || "");
  const render = String(intake.render || "");
  if (render === "webpage") return intake.title ? String(intake.title).slice(0, 48) : "web page";
  if (kind === "audio") {
    const n = Number(intake.transcript_chars || 0);
    return n ? `${n.toLocaleString()} char transcript` : "audio";
  }
  if (kind === "pdf") {
    const p = Number(intake.page_count || 0);
    return p ? `${p} pages` : "pdf";
  }
  if (kind === "image") return intake.caption ? String(intake.caption).slice(0, 48) : "image";
  const lines = Number(intake.line_count || 0);
  return lines ? `${lines} lines` : (kind || null);
}

/** The artifact carries actual image bytes (a data-URL or bare base64) rather
 *  than a text caption — so it can be shown as an <img>. Plain string checks
 *  (no regex per project rule). */
function imageSrcForArtifact(a: ArtifactProps["artifact"], content: string): string | null {
  if (a.type !== "intake_image" && a.type !== "image") return null;
  const c = content.trim();
  if (c.startsWith("data:image/")) return c;
  // Bare base64 (no data-URL prefix): only treat long, non-whitespace payloads
  // as image bytes so a short caption never gets mis-rendered as a broken image.
  if (c.length > 256 && !c.includes(" ") && !c.includes("\n")) {
    const mime = String((a.intake?.mime as string) || "") || "image/png";
    return `data:${mime};base64,${c}`;
  }
  return null;
}

function isRenderableCodeArtifact(a: ArtifactProps["artifact"], lang: string): boolean {
  const t = (a.type || "").toLowerCase();
  if (t === "apex_class" || t === "apex_trigger" || t === "apex" || t === "code") return true;
  const l = (lang || "").toLowerCase();
  return ["apex", "javascript", "typescript", "html", "css", "xml"].includes(l);
}

interface ArtifactProps {
  artifact: {
    id: string;
    type: string;
    name: string;
    toolName: string;
    byteSize: number;
    createdAt: number;
    intake?: Record<string, unknown> | null;
  };
  /** When true, card starts expanded (e.g. code patches). */
  defaultOpen?: boolean;
  /** Called when user collapses from an inline citation wrapper. */
  onCollapse?: () => void;
  /** F12: render only the content (no header strip / toggle) — used INSIDE the
   *  layover, where the dialog supplies the title + close. Forces open. */
  hideHeader?: boolean;
  /** F12: hide "Add to Workspace" / "Compare" — those belong to the inline chat
   *  cards, not the layover preview. */
  hideWorkspaceActions?: boolean;
}

// Memoized: ResultColumn re-renders on every streaming token; without this each
// open card (and its Monaco editor) would re-render on the parent's churn even
// though the artifact's own props never changed.
export const ArtifactCard = memo(function ArtifactCard({ artifact: a, defaultOpen, onCollapse, hideHeader, hideWorkspaceActions }: ArtifactProps) {
  const displayName = sanitizeArtifactLabel(a.name, a.id.slice(0, 8));
  const isPatchArtifact = a.type === "code_patch" || a.type === "diff";
  // Start collapsed by default; callers that want a card open (e.g. past-turn
  // code artifacts in ChatPanel) pass defaultOpen explicitly.
  const [open, setOpen] = useState(hideHeader ? true : (defaultOpen ?? false));
  // F12: pop this artifact into the full-size layover (button lives in the header,
  // so it's naturally absent inside the overlay's own card via hideHeader).
  const openOverlay = useArtifactOverlayStore((s) => s.open);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ArtifactContent | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const { copied, copy: copyText } = useCopyToClipboard();

  const load = async () => {
    if (!data && !loading) {
      setLoading(true);
      setErr(null);
      try {
        const c = await getArtifact(a.id);
        setData(c);
      } catch (e) {
        setErr((e as Error).message);
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (open) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, a.id]);

  const collapse = () => {
    setOpen(false);
    onCollapse?.();
  };

  const toggle = () => {
    if (open) {
      collapse();
      return;
    }
    setOpen(true);
  };

  const copy = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (data?.content) void copyText(data.content);
  };

  const lang = fenceLang(a, data);
  const body = data ? maybePretty(data.content) : "";
  const fenced = data ? "```" + lang + "\n" + body + "\n```" : "";
  // Salesforce Flow → render as a diagram (with a Source toggle). The .flow XML is
  // either the raw content or a fix `source` inside a code_patch.
  const flowXml = useMemo(() => (data ? extractFlowXml(a, body) : null), [data, a, body]);
  const [flowMode, setFlowMode] = useState<"diagram" | "source">("diagram");
  // Generated LWC bundle → a "launch and show" live-preview button. The chat
  // backend launches `sf lightning dev` against the org and returns a URL.
  const lwcBundle = useMemo(() => (data ? extractLwcBundle(body) : null), [data, body]);

  return (
    <div
      className="ma-fade-up overflow-hidden"
      style={{
        background: "var(--ma-grad-surface)",
        borderRadius: "var(--ma-radius-md)",
        boxShadow: "var(--ma-shadow-sm)",
        border: "1px solid var(--ma-border)",
      }}
    >
      {!hideHeader && (
      <div className="flex w-full items-center gap-2 px-2.5 py-2">
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-2 text-left transition-colors hover:opacity-90"
          onClick={toggle}
          aria-expanded={open}
        >
          {open ? (
            <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          )}
          {(() => {
            const Icon = iconForArtifactType(a.type);
            return <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />;
          })()}
          <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] uppercase text-muted-foreground">
            {a.type || "artifact"}
          </span>
          <span className="min-w-0 flex-1 truncate text-[12px] font-medium text-foreground/90">
            {displayName}
          </span>
          {(() => {
            const chip = intakeChip(a.intake);
            return chip ? (
              <span
                className="hidden max-w-[160px] shrink-0 truncate rounded bg-muted/60 px-1.5 py-0.5 text-[11px] text-muted-foreground/80 sm:inline"
                title={chip}
              >
                {chip}
              </span>
            ) : null;
          })()}
          <span className="hidden shrink-0 font-mono text-[11px] text-muted-foreground/70 sm:inline">
            {a.toolName}
          </span>
          <span className="shrink-0 font-mono text-[11px] text-muted-foreground/60">
            {formatBytes(a.byteSize)}
          </span>
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); openOverlay(a); }}
          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
          title="Open in layover"
          aria-label="Open artifact in layover"
        >
          <Maximize2 className="h-3.5 w-3.5" />
        </button>
        {open && (
          <button
            type="button"
            onClick={collapse}
            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
            title="Collapse"
            aria-label="Collapse artifact"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      )}

      {open && (
        <div className={hideHeader ? "overflow-hidden px-3 py-3" : "border-t border-border overflow-hidden px-3 py-3"}>
          {loading && (
            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              loading…
            </div>
          )}
          {err && (
            <div className="text-[11px]" style={{ color: "var(--ma-state-err)" }}>
              failed to load: {err}
            </div>
          )}
          {lwcBundle && <LwcPreviewButton bundle={lwcBundle} />}
          {data && flowXml
            ? (
              <>
                <div className="mb-1.5 flex items-center gap-1">
                  <button type="button" onClick={() => setFlowMode("diagram")}
                    className={`rounded-md px-2 py-0.5 text-[10px] ${flowMode === "diagram" ? "bg-muted text-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}>
                    Diagram
                  </button>
                  <button type="button" onClick={() => setFlowMode("source")}
                    className={`rounded-md px-2 py-0.5 text-[10px] ${flowMode === "source" ? "bg-muted text-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}>
                    Source
                  </button>
                </div>
                {flowMode === "diagram"
                  ? <FlowDiagramView xml={flowXml} />
                  : <CodeArtifactView code={flowXml} name={displayName} language="xml" copy={copy} copied={copied} hideWorkspaceActions={hideWorkspaceActions} />}
              </>
            )
            : data && imageSrcForArtifact(a, data.content)
            ? <ImageArtifactView src={imageSrcForArtifact(a, data.content)!} name={displayName} onEnlarge={hideHeader ? undefined : () => openOverlay(a)} />
            : data && isPatchArtifact
            ? <CodePatchView raw={body} copy={copy} copied={copied} hideWorkspaceActions={hideWorkspaceActions} />
            : data && isExternalRecordArtifact(a.type)
              ? <ExternalRecordView raw={body} copy={copy} copied={copied} />
            : data && isDataResultArtifact(a.type)
              ? <DataResultView raw={body} copy={copy} copied={copied} artifactType={a.type || "data_result"} />
            : data && isRenderableCodeArtifact(a, lang)
              ? (
                <CodeArtifactView
                  code={body}
                  name={displayName}
                  language={lang}
                  copy={copy}
                  copied={copied}
                  hideWorkspaceActions={hideWorkspaceActions}
                />
              )
            : data && (
              <>
                <div className="mb-1.5 flex items-center justify-end gap-1">
                  <button
                    type="button"
                    onClick={copy}
                    className="rounded-md px-2 py-0.5 text-[10px] text-muted-foreground hover:bg-muted hover:text-foreground"
                    title="Copy content"
                  >
                    <Copy className="inline h-3 w-3" /> {copied ? "copied" : "copy"}
                  </button>
                </div>
                <SkillResultMarkdown content={fenced} isStreaming={false} />
              </>
            )
          }
        </div>
      )}
    </div>
  );
});

function isDataResultArtifact(type: string | undefined): boolean {
  return ["soql_result", "tooling_query_result", "tooling_result", "data_result", "query_result"].includes((type || "").toLowerCase());
}

function isExternalRecordArtifact(type: string | undefined): boolean {
  return (type || "").toLowerCase() === "external_record";
}

/** The NEW content a code_patch fix produces. `source` is normally an object
 *  ({original, replacement, unified_diff}); we want the replacement. Falls back
 *  to original, a legacy string `source`, or after/content. */
function fixNewContent(f: { source?: unknown; after?: unknown; content?: unknown }): string {
  const s = f?.source;
  if (typeof s === "string") return s; // legacy string shape
  const obj = s as { replacement?: unknown; original?: unknown } | undefined;
  const pick = obj?.replacement ?? obj?.original ?? f?.after ?? f?.content ?? "";
  return typeof pick === "string" ? pick : "";
}

/** Extract a Salesforce Flow's `.flow-meta.xml` from an artifact — either the raw
 *  content, or a fix's new content inside a code_patch JSON. null when not a Flow. */
function extractFlowXml(a: { type?: string; name?: string }, body: string): string | null {
  const isFlowXml = (s: string) => !!s && s.indexOf("<Flow") !== -1 && s.indexOf("</Flow>") !== -1;
  if (isFlowXml(body) && looksLikeFlow({ type: a.type, name: a.name, content: body })) return body;
  try {
    const parsed = JSON.parse(body);
    const fixes = parsed?.fixes ?? parsed?.changes ?? [];
    if (Array.isArray(fixes)) {
      for (const f of fixes) {
        const src = fixNewContent(f);
        if (isFlowXml(src)) return src;
      }
    }
  } catch { /* not JSON — fine */ }
  return null;
}

interface LwcBundle { component_name: string; files: Record<string, string> }

/** Coerce any name to a VALID LWC bundle name (the dev-server rejects others with a
 *  400): lowercase first letter, only letters/numbers/underscore, no leading/trailing/
 *  double underscores. Plain scan, no regex. */
function toLwcBundleName(name: string): string {
  let s = "";
  for (const c of name) {
    if ((c >= "a" && c <= "z") || (c >= "A" && c <= "Z") || (c >= "0" && c <= "9") || c === "_") s += c;
  }
  if (s && s[0] >= "A" && s[0] <= "Z") s = s[0].toLowerCase() + s.slice(1);
  while (s.indexOf("__") !== -1) s = s.split("__").join("_");
  while (s.startsWith("_")) s = s.slice(1);
  while (s.endsWith("_")) s = s.slice(0, -1);
  if (s && !(s[0] >= "a" && s[0] <= "z")) s = "c" + s;
  return s || "previewComponent";
}

/** Pull a generated LWC bundle out of a code_patch artifact — the files under a
 *  `.../lwc/<name>/` folder (or fixes flagged LightningComponentBundle). Returns
 *  the first component's { component_name, files }, or null when there isn't one.
 *  Plain string parsing only (no regex / split). */
function extractLwcBundle(body: string): LwcBundle | null {
  let parsed: unknown;
  try { parsed = JSON.parse(body); } catch { return null; }
  const fixes = (parsed as { fixes?: unknown; changes?: unknown })?.fixes
    ?? (parsed as { changes?: unknown })?.changes;
  if (!Array.isArray(fixes)) return null;

  let component = "";
  const files: Record<string, string> = {};
  for (const f of fixes) {
    const file = String((f?.file ?? f?.path ?? "") as unknown);
    const mdt = String((f?.metadata_type ?? f?.metadataType ?? "") as unknown);
    const idx = file.indexOf("lwc/");
    const isLwc = idx !== -1 || mdt === "LightningComponentBundle";
    const content = fixNewContent(f);
    if (!isLwc || !content) continue;

    // component name: prefer the explicit field, then the "lwc/<name>/" path segment
    let comp = String((f?.component_name ?? "") as unknown);
    if (!comp && idx !== -1) {
      const rest = file.slice(idx + 4);
      const slash = rest.indexOf("/");
      comp = slash >= 0 ? rest.slice(0, slash) : "";
    }
    // filename = basename of the path
    const ls = file.lastIndexOf("/");
    const fname = ls >= 0 ? file.slice(ls + 1) : file;
    if (!fname) continue;  // need a filename to place the bundle file
    if (!comp) {  // fall back to the basename before the first dot (previewCard.js → previewCard)
      const dot = fname.indexOf(".");
      comp = dot >= 0 ? fname.slice(0, dot) : fname;
    }
    if (!component) component = comp;
    if (comp !== component) continue;  // first component only
    files[fname] = content;
  }
  // need at least the JS controller for the dev server to render the bundle
  const hasJs = Object.keys(files).some((n) => n.indexOf(".js") !== -1);
  if (!component || !hasJs) return null;
  // The dev-server requires EVERY bundle file to be named after the (sanitized)
  // bundle name — e.g. lWC_SCR_Registration.js / .html / .js-meta.xml. The source
  // paths still carry the ORIGINAL base (LWC_SCR_Registration.js), so rebase each
  // filename onto the bundle name, preserving its extension. Split at the FIRST
  // dot so compound suffixes like ".js-meta.xml" stay intact. Plain string ops.
  const bundleName = toLwcBundleName(component);
  const renamedFiles: Record<string, string> = {};
  for (const fname of Object.keys(files)) {
    const dot = fname.indexOf(".");
    const suffix = dot >= 0 ? fname.slice(dot) : "";
    renamedFiles[bundleName + suffix] = files[fname];
  }
  return { component_name: bundleName, files: renamedFiles };
}

/** "Launch and show" button for a generated LWC — asks the chat backend to start
 *  `sf lightning dev` against the org and opens the returned preview URL in a new
 *  tab (top-level nav, so the org's frame-ancestors CSP doesn't block it). */
function LwcPreviewButton({ bundle }: { bundle: LwcBundle }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const launch = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await startLwcPreview(bundle);
      window.open(r.preview_url, "_blank", "noopener,noreferrer");
    } catch (e) {
      setError(e instanceof Error ? e.message : "preview failed");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="mb-2 flex items-center gap-2">
      <button
        type="button"
        onClick={launch}
        disabled={loading}
        className="inline-flex items-center gap-1.5 rounded-md border border-primary bg-primary px-3 py-1.5 text-[11px] font-semibold text-primary-foreground shadow-sm hover:bg-primary/90 disabled:opacity-60"
        title={`Launch a live preview of ${bundle.component_name} against your org`}
      >
        {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <ExternalLink className="h-3 w-3" />}
        {loading ? "Launching preview…" : "Open Live Preview"}
      </button>
      {error && <span className="text-[10px]" style={{ color: "var(--ma-state-err)" }}>{error}</span>}
    </div>
  );
}

/** Renders an `external_record` artifact — a record pulled from a connected MCP
 * system (Jira/Notion/Linear/…). Content is the normalized ExternalRecord JSON
 * ({system,type,id,title,url,fields,content,version}); falls back to raw markdown
 * if it isn't valid JSON. Read-only: this card never writes back. */
function ExternalRecordView({
  raw,
  copy,
  copied,
}: {
  raw: string;
  copy: (e: React.MouseEvent) => void;
  copied: boolean;
}) {
  let rec: {
    system?: string; type?: string; id?: string; title?: string;
    url?: string; version?: string;
    fields?: Record<string, unknown>; content?: string;
  } | null = null;
  try { rec = JSON.parse(raw); } catch { /* raw fallback below */ }
  if (!rec) {
    return <SkillResultMarkdown content={raw} isStreaming={false} />;
  }
  const fields = rec.fields && typeof rec.fields === "object" ? rec.fields : {};
  const fieldKeys = Object.keys(fields);
  const ident = [rec.system, rec.type, rec.id].filter(Boolean).join(" · ");
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        {ident && (
          <span className="rounded bg-primary/10 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-primary">
            {ident}
          </span>
        )}
        {rec.version && (
          <span className="text-[10px] text-muted-foreground" title="version / concurrency token">
            v{rec.version}
          </span>
        )}
        {rec.url && (
          <a
            href={rec.url}
            target="_blank"
            rel="noreferrer noopener"
            className="ml-auto inline-flex items-center gap-1 text-[11px] text-primary hover:underline"
          >
            <Link2 className="h-3 w-3" /> Open in {rec.system || "source"}
          </a>
        )}
        <button
          type="button"
          onClick={copy}
          className="rounded-md px-2 py-0.5 text-[10px] text-muted-foreground hover:bg-muted hover:text-foreground"
          title="Copy record JSON"
        >
          <Copy className="inline h-3 w-3" /> {copied ? "copied" : "copy"}
        </button>
      </div>
      {rec.title && <div className="text-sm font-semibold text-foreground">{rec.title}</div>}
      {fieldKeys.length > 0 && (
        <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 rounded-md border border-border p-2 text-xs">
          {fieldKeys.map((k) => (
            <Fragment key={k}>
              <span className="font-medium text-muted-foreground">{k}</span>
              <span className="break-words text-foreground">{String((fields as Record<string, unknown>)[k] ?? "")}</span>
            </Fragment>
          ))}
        </div>
      )}
      {rec.content && (
        <div className="max-h-[420px] overflow-auto rounded-md border border-border p-2">
          <SkillResultMarkdown content={rec.content} isStreaming={false} />
        </div>
      )}
    </div>
  );
}

/**
 * Row-source precedence (`full_list` > `results` > `rows` > `records`) lives in
 * `services/multi-agent/dataRows` because the report exporter's cited-evidence
 * appendix must resolve the SAME rows this card shows — a second copy here is a
 * second thing to keep in step, and the failure mode is a downloaded file that
 * disagrees with the card it came from. Re-exported so this module stays the
 * import surface the card's tests already use.
 */
export { pickDataRows };

/** Exported for its own tests (as CodePatchFixView is) — the subset-note rule below
 *  is only observable in the rendered markup. */
export function DataResultView({
  raw,
  copy,
  copied,
  artifactType,
}: {
  raw: string;
  copy: (e: React.MouseEvent) => void;
  copied: boolean;
  artifactType: string;
}) {
  let parsed: {
    rows?: Array<Record<string, unknown>>;
    columns?: string[];
    query?: string;
    totalSize?: number;
  } | null = null;
  try { parsed = JSON.parse(raw); } catch { /* raw fallback below */ }
  const { rows: allRows, total } = pickDataRows(parsed);
  // Column rule comes from the shared module, not a second inline copy — an inline
  // copy of the ROW rule is how the `full_list` blindness got here in the first place.
  const columns = dataColumns(allRows, parsed?.columns);
  const shownRows = allRows.slice(0, 50);
  const download = (format: "json" | "csv") => {
    // JSON export dumps the raw artifact body as-is — it's already the full payload.
    // CSV export uses the full chosen row source, not just the 50-row preview.
    const content = format === "json" ? raw : toCsv(allRows, columns);
    const blob = new Blob([content], { type: format === "json" ? "application/json" : "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${artifactType || "data_result"}.${format}`;
    a.click();
    URL.revokeObjectURL(url);
  };
  if (!parsed) {
    return <SkillResultMarkdown content={"```json\n" + raw + "\n```"} isStreaming={false} />;
  }
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-foreground">
          {total.toLocaleString()} rows
        </span>
        {parsed.query && (
          <span className="max-w-[70%] truncate font-mono text-[10px] text-muted-foreground" title={parsed.query}>
            {parsed.query}
          </span>
        )}
        <div className="ml-auto flex items-center gap-1">
          <button type="button" onClick={copy} className="rounded-md px-2 py-0.5 text-[10px] text-muted-foreground hover:bg-muted hover:text-foreground">
            <Copy className="inline h-3 w-3" /> {copied ? "copied" : "copy"}
          </button>
          <button type="button" onClick={() => download("csv")} className="rounded-md px-2 py-0.5 text-[10px] text-muted-foreground hover:bg-muted hover:text-foreground">
            CSV
          </button>
          <button type="button" onClick={() => download("json")} className="rounded-md px-2 py-0.5 text-[10px] text-muted-foreground hover:bg-muted hover:text-foreground">
            JSON
          </button>
        </div>
      </div>
      {shownRows.length === 0 ? (
        // A bare aggregate (SELECT COUNT() ...) returns no rows — its value is only in total.
        <div className="rounded-md border border-border px-3 py-6 text-center text-xs text-muted-foreground">
          {total > 0
            ? `Aggregate result: ${total.toLocaleString()} — no row data returned`
            : "No records returned"}
        </div>
      ) : (
        <div className="max-h-[520px] overflow-auto rounded-md border border-border">
          <table className="w-full border-collapse text-xs">
            <thead className="sticky top-0 bg-muted text-left">
              <tr>
                {columns.map((c) => (
                  <th key={c} className="border-b border-border px-2 py-1.5 font-medium text-muted-foreground">
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shownRows.map((row, i) => (
                <tr key={i} className="odd:bg-background even:bg-muted/20">
                  {columns.map((c) => (
                    <td key={c} className="max-w-[260px] truncate border-b border-border/50 px-2 py-1.5 align-top" title={displayCell(readDataCell(row, c))}>
                      {displayCell(readDataCell(row, c))}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* Gated on the DECLARED total, not on what the payload happens to carry: a
          capped body (30 `results` rows under `total_count: 5000`) is still a subset,
          and printing "5,000 rows" over 30 with no note is the exact mislabel this
          card is supposed to prevent. */}
      {total > shownRows.length && (
        <div className="text-[11px] text-muted-foreground">
          Showing first {shownRows.length.toLocaleString()} of {total.toLocaleString()} rows.
          Download CSV or JSON for the full result.
        </div>
      )}
    </div>
  );
}

// `displayCell` / `readDataCell` moved to services/multi-agent/dataRows alongside
// the row-source rule — the exported appendix renders the same cells this table
// does, dotted SOQL paths and related-record Names included.

function toCsv(rows: Array<Record<string, unknown>>, columns: string[]): string {
  const esc = (v: unknown) => {
    const s = displayCell(v);
    const needsQuote =
      s.includes('"') || s.includes(",") || s.includes("\n") || s.includes("\r");
    return needsQuote ? `"${s.split('"').join('""')}"` : s;
  };
  return [columns.map(esc).join(","), ...rows.map((r) => columns.map((c) => esc(readDataCell(r, c))).join(","))].join("\n");
}

// Typed code_patch / diff renderer: reads the structured shape emitted
// by auto_fix.propose (file / metadata_type / source.{original,
// replacement, unified_diff} / test_class / dependencies / risk /
// validation_status). The body is JSON we pretty-printed upstream;
// parse and render fields + diff fences + Apply button.
interface CodePatchFix {
  kind?: string;
  file?: string;
  component_name?: string;
  metadata_type?: string;
  summary?: string;
  rationale?: string;
  risk?: string;
  test_class?: string | null;
  dependencies?: string[];
  validation_status?: string;
  source?: { original?: string; replacement?: string; unified_diff?: string };
  // UUID linkage (dev-agent parity): the artifact id the ORIGINAL source came
  // from. Lets the UI resolve "current" by id via GET /api/artifacts/{id}
  // instead of relying on a class name when the body wasn't inlined.
  source_artifact_id?: string;
  // Legacy single-diff shape (pre-iter14) — still rendered if present.
  unified_diff?: string;
}

function CodePatchView({ raw, copy, copied, hideWorkspaceActions }: { raw: string; copy: (e: React.MouseEvent) => void; copied: boolean; hideWorkspaceActions?: boolean }) {
  let parsed: { fixes?: CodePatchFix[]; topic?: string } | null = null;
  try { parsed = JSON.parse(raw); } catch { /* keep null */ }
  const fixes = parsed?.fixes || [];
  if (!fixes.length) {
    // Fallback to plain rendering if the shape doesn't match.
    return (
      <div>
        <div className="mb-1.5 flex justify-end">
          <button
            type="button"
            onClick={copy}
            className="rounded-md px-2 py-0.5 text-[10px] text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <Copy className="inline h-3 w-3" /> {copied ? "copied" : "copy"}
          </button>
        </div>
        <SkillResultMarkdown content={"```json\n" + raw + "\n```"} isStreaming={false} />
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      {fixes.map((f, i) => <CodePatchFixView key={i} f={f} hideWorkspaceActions={hideWorkspaceActions} />)}
    </div>
  );
}


/**
 * DiffEditorContainer — owns Monaco layout correctness.
 *
 * Why this exists:
 * - Monaco reads its container's offsetWidth to set the split-pane widths.
 *   If any flex ancestor lacks min-width:0, that read returns a value wider
 *   than the available space and the left panel bleeds into the sidebar.
 * - overflow:hidden on the wrapper clips any momentary bleed while the flex
 *   layout resolves on first paint.
 * - The ResizeObserver calls editor.layout() whenever the card width changes
 *   (sidebar open/close, window resize) so the split always stays correct.
 * - renderSideBySide switches to false below 640px so the diff remains
 *   usable on laptop / mobile screens.
 */
function DiffEditorContainer({
  original,
  replacement,
  language,
  monacoTheme,
  beforeMount,
}: {
  original: string;
  replacement: string;
  language: string;
  monacoTheme: string;
  beforeMount?: (monaco: Monaco) => void;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<Parameters<NonNullable<React.ComponentProps<typeof DiffEditor>["onMount"]>>[0] | null>(null);
  const [sideBySide, setSideBySide] = useState(true);

  // Switch to inline diff on narrow containers (< 640px).
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        const w = e.contentRect.width;
        setSideBySide(w >= 640);
        // Force Monaco to recalculate — catches sidebar expand/collapse.
        editorRef.current?.layout();
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Adaptive height: scale with the larger side, capped at 560px / min 260px.
  // Side-by-side shows both halves at this height; inline diff uses ~the same lines.
  const maxLines = Math.max(lineCount(original), lineCount(replacement));
  const editorHeight = Math.min(560, Math.max(260, maxLines * 18 + 60));

  return (
    <div className="mb-3">
      <div className="mb-1.5 flex items-center gap-2 ma-micro" style={{ letterSpacing: 0, textTransform: "none" }}>
        <span style={{ color: "var(--ma-state-ok)" }}>current</span>
        <span style={{ color: "var(--ma-text-muted)" }}>→</span>
        <span style={{ color: "var(--ma-state-err)" }}>new</span>
        <span style={{ marginLeft: "auto", color: "var(--ma-text-muted)" }}>
          {lineCount(original)} → {lineCount(replacement)} lines
        </span>
      </div>
      {/* overflow:hidden + min-width:0 prevent Monaco bleed past this card. */}
      <div
        ref={wrapRef}
        style={{ overflow: "hidden", minWidth: 0, width: "100%", borderRadius: 4 }}
      >
        <DiffEditor
          height={`${editorHeight}px`}
          width="100%"
          language={language}
          original={original}
          modified={replacement}
          theme={monacoTheme}
          beforeMount={beforeMount}
          onMount={(editor) => {
            editorRef.current = editor;
            requestAnimationFrame(() => editor.layout());
          }}
          options={{
            readOnly: true,
            renderSideBySide: sideBySide,
            renderOverviewRuler: false,
            diffWordWrap: "off",
            ignoreTrimWhitespace: false,
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            wordWrap: "off",
            automaticLayout: true,
          }}
        />
      </div>
    </div>
  );
}

/**
 * The `validation_status` values that mean "the server already cleared this source".
 *
 * TWO VOCABULARIES REACH THIS FIELD. The studio / IDE save path speaks
 * `validated | validation_failed | unvalidated` (see `lib/studio/validation.ts`,
 * and `ApexValidateResult.validation_status` from `/validate-save`), while the
 * multi-agent `code_patch` fixes speak the enum in `VALIDATION_COLOR`:
 * `unverified | compiles | runs_tests_pass | runs_tests_fail`. Checking only for
 * `"validated"` would leave the hide-case dead on the multi-agent surface — the
 * exact cards this control clutters — so BOTH success spellings count.
 *
 * Deliberately excluded: `runs_tests_fail`, `validation_failed`, `unverified`,
 * `unvalidated`, and absent — a failed or never-run check is precisely when a
 * manual re-run has something to offer.
 */
export const VALIDATED_STATUSES: ReadonlySet<string> = new Set([
  "validated",        // studio / IDE vocabulary
  "compiles",         // multi-agent: compile-check passed
  "runs_tests_pass",  // multi-agent: compile + tests passed
]);

/**
 * Whether a code-patch fix should still offer the manual re-validate control.
 *
 * The server auto-validates every emitted fix, so the button is NOT the thing that
 * makes a card trustworthy — it is a second, redundant round-trip. It earns its
 * place in exactly two situations:
 *
 *  - `contentEdited` — the source shown on the card no longer matches the source the
 *    server validated (today that happens when a validation auto-fix splices new
 *    content in via `fixedCode`), so the recorded verdict no longer describes what
 *    the user is looking at; or
 *  - the recorded `validation_status` is not one of `VALIDATED_STATUSES` — nothing
 *    has cleared this source yet, or the check came back failing.
 *
 * A pristine, already-cleared fix shows its status chip and no button.
 *
 * Exported for unit tests: the render tests cover the prop-reachable states, this
 * predicate covers the `contentEdited` axis, which lives in component state.
 */
export function shouldShowRevalidate(opts: {
  canValidate: boolean;
  contentEdited: boolean;
  validationStatus?: string | null;
}): boolean {
  if (!opts.canValidate) return false;
  return opts.contentEdited || !VALIDATED_STATUSES.has(opts.validationStatus ?? "");
}

export const CodePatchFixView = memo(function CodePatchFixView({ f, hideWorkspaceActions }: { f: CodePatchFix; hideWorkspaceActions?: boolean }) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const monacoTheme = isDark ? "ncoder-dark" : "ncoder-light";
  const diffMonacoRef = useRef<Monaco | null>(null);

  const beforeDiffMount = useCallback((monaco: Monaco) => {
    diffMonacoRef.current = monaco;
    registerAppMonacoThemes(monaco, document.documentElement.classList.contains("dark"));
  }, []);

  useEffect(() => {
    if (diffMonacoRef.current) {
      registerAppMonacoThemes(diffMonacoRef.current, isDark);
    }
  }, [isDark]);
  const { copied: applied, copy: copyPatch } = useCopyToClipboard(1600);
  const diff = f.source?.unified_diff || f.unified_diff || "";
  const inlineOriginal = f.source?.original || "";
  const replacement = f.source?.replacement || "";
  const language = languageForPatch(f);
  // A delete has NO new body BY DESIGN (a destructiveChanges removal, not a
  // missing/incomplete patch) — `replacement === ""` must not be read as
  // "nothing to act on" anywhere below.
  const isDelete = f.kind === "delete";
  // On-demand Re-validate button (validate-only, never saves): compile-check the shown
  // Apex + auto-fix, show the result, and swap in the fixed source. runId comes from
  // the active run in the store; the button only shows for apex fixes, and only when
  // the card content has diverged from the server-validated source or no success
  // verdict ever cleared that source (see shouldShowRevalidate).
  const runId = useMultiAgentStore((s) => s.runId);
  const [validating, setValidating] = useState(false);
  const [vres, setVres] = useState<ApexValidateResult | null>(null);
  const [fixedCode, setFixedCode] = useState<string | null>(null);

  // UUID-FIRST "current" source (dev-agent parity): when the backend recorded a
  // source_artifact_id but didn't inline the original body, resolve it by id via
  // the SAME artifact endpoint the cards already use — never by class name. This
  // guarantees the mandatory side-by-side current-vs-new diff renders.
  const [fetchedOriginal, setFetchedOriginal] = useState("");
  const hasInlineOriginal = inlineOriginal.trim().length > 0;
  useEffect(() => {
    let alive = true;
    setFetchedOriginal(""); // reset on id change so a stale body never pairs with a new fix
    const id = f.source_artifact_id;
    if (hasInlineOriginal || !id) return;
    getArtifact(id)
      .then((a) => { if (alive) setFetchedOriginal(a?.content || ""); })
      .catch((e) => {
        console.warn("[ArtifactCard] resolve current source by id failed", id, e);
        if (alive) setFetchedOriginal("");
      });
    return () => { alive = false; };
  }, [f.source_artifact_id, hasInlineOriginal]);

  const original = inlineOriginal.trim() ? inlineOriginal : fetchedOriginal;
  const hasCompletePair =
    original.trim().length > 0 && (replacement.trim().length > 0 || isDelete);
  const actionName =
    extractApexName(replacement) ||
    f.component_name ||
    bareClassName(f.file) ||
    f.file ||
    "GeneratedApex";
  const actionMetadataType = inferMetadataType(f.file || actionName, replacement, f.metadata_type || "ApexClass");

  const shownReplacement = fixedCode ?? replacement;
  // "user has edited the card content": the source rendered on the card is no longer
  // the source the server validated. The card's editors are read-only, so the only
  // in-card mutation today is a validation auto-fix splice (fixedCode) — keeping this
  // as a derived comparison means any future in-card edit path flips it for free.
  const contentEdited = shownReplacement !== replacement;
  const canValidate = Boolean(runId) && Boolean(replacement) && language === "apex";
  const showRevalidate = shouldShowRevalidate({
    canValidate,
    contentEdited,
    validationStatus: f.validation_status,
  });
  const onValidate = async () => {
    if (!runId || validating || !replacement) return;
    setValidating(true);
    setVres(null);
    try {
      const r = await validateApex(runId, actionName, fixedCode ?? replacement, actionMetadataType);
      setVres(r);
      if (r.content && r.content !== (fixedCode ?? replacement)) setFixedCode(r.content);
    } catch (e) {
      setVres({ valid: false, saved: false, attempts: 0, errors: [{ message: (e as Error).message }] });
    } finally {
      setValidating(false);
    }
  };

  const apply = () => {
    // v1: copy the diff to clipboard so the user can paste into their
    // org / VS Code SF extension. Full deploy-on-click is a follow-up
    // that needs the SF metadata API wired through the proxy.
    void copyPatch(diff || replacement || "");
  };

  return (
    <div
      className="ma-fade-up min-w-0"
      style={{
        background: "var(--ma-grad-surface)",
        borderRadius: "var(--ma-radius-md)",
        boxShadow: "var(--ma-shadow-sm)",
        padding: "10px 12px",
      }}
    >
      {/* Metadata header */}
      <div className="mb-2 flex flex-wrap items-center gap-2">
        {f.kind && (
          <span className="ma-micro" style={{
            padding: "2px 6px", borderRadius: 4,
            background: "var(--ma-surface-soft)",
            textTransform: "lowercase", letterSpacing: 0,
            color: "var(--ma-text-muted)",
          }}>
            {f.kind}
          </span>
        )}
        {f.file && (
          <span style={{
            fontSize: 12, fontWeight: 500, color: "var(--ma-text)",
            fontFamily: "ui-monospace, monospace",
          }}>
            {f.file}
          </span>
        )}
        {f.metadata_type && (
          <span className="ma-micro" style={{
            textTransform: "none", letterSpacing: 0, color: "var(--ma-text-muted)",
          }}>
            · {f.metadata_type}
          </span>
        )}
        {f.risk && (
          <span className="ma-micro" style={{
            marginLeft: "auto",
            padding: "2px 8px", borderRadius: 999,
            background: `color-mix(in oklab, ${RISK_COLOR[f.risk] || "var(--ma-text-muted)"} 14%, transparent)`,
            color: RISK_COLOR[f.risk] || "var(--ma-text-muted)",
            fontWeight: 600,
          }}>
            {f.risk}
          </span>
        )}
      </div>

      {/* Summary + rationale */}
      {f.summary && (
        <div style={{ fontSize: 13, color: "var(--ma-text)", marginBottom: 6 }}>
          {f.summary}
        </div>
      )}
      {f.rationale && (
        <div style={{ fontSize: 11, color: "var(--ma-text-muted)", marginBottom: 8, lineHeight: 1.5 }}>
          {f.rationale}
        </div>
      )}

      {hasCompletePair ? (
        // De-boxed: a single side-by-side diff IS the before→after. The wrapper
        // gets overflow:hidden + min-width:0 so Monaco never bleeds past the
        // card boundary or into the sidebar when flex layout is still resolving.
        <DiffEditorContainer
          original={original}
          replacement={shownReplacement}
          language={language}
          monacoTheme={monacoTheme}
          beforeMount={beforeDiffMount}
        />
      ) : diff ? (
        <div
          className="mb-2 rounded-md p-3 min-w-0"
          style={{
            border: "1px solid color-mix(in oklab, var(--ma-state-warn) 40%, transparent)",
            background: "color-mix(in oklab, var(--ma-state-warn) 8%, var(--ma-surface))",
          }}
        >
          <div className="mb-2 text-[11px]" style={{ color: "color-mix(in oklab, var(--ma-state-warn) 80%, var(--ma-text))" }}>
            This patch has a diff but is missing complete original/replacement source. It cannot be applied as a complete-code artifact.
          </div>
          <div className="min-w-0">
            <SkillResultMarkdown content={"```diff\n" + diff + "\n```"} isStreaming={false} />
          </div>
        </div>
      ) : replacement ? (
        <div className="mb-2">
          <div className="mb-1.5 ma-micro" style={{ color: "var(--ma-state-err)", letterSpacing: 0 }}>
            New complete source{fixedCode ? " · auto-fixed" : ""}
          </div>
          <InlineMonaco value={shownReplacement} language={language} tone="new" />
        </div>
      ) : null}

      {/* Footer: status + apply + deps + test class */}
      <div className="flex flex-wrap items-center gap-2 pt-2" style={{ borderTop: "1px solid var(--ma-border)" }}>
        {f.validation_status && (
          <span className="ma-micro" style={{
            textTransform: "none", letterSpacing: 0,
            color: VALIDATION_COLOR[f.validation_status] || "var(--ma-text-muted)",
          }}>
            ● {humanizeStatus(f.validation_status)}
          </span>
        )}
        {f.test_class && (
          <span className="ma-micro" style={{
            textTransform: "none", letterSpacing: 0,
            color: "var(--ma-text-muted)",
            fontFamily: "ui-monospace, monospace",
          }}>
            tests: {f.test_class}
          </span>
        )}
        {(f.dependencies && f.dependencies.length > 0) && (
          <span className="ma-micro" style={{
            textTransform: "none", letterSpacing: 0, color: "var(--ma-text-muted)",
          }}>
            deps: {f.dependencies.join(", ")}
          </span>
        )}
        <div className="ml-auto flex flex-wrap items-center gap-1.5">
          {vres && (
            <span className="ma-micro" style={{
              textTransform: "none", letterSpacing: 0,
              color: vres.valid ? "var(--ma-state-ok, #16a34a)" : "var(--ma-state-err)",
            }}>
              {vres.valid
                ? (vres.attempts > 1 ? `✓ valid · auto-fixed (${vres.attempts})` : "✓ valid")
                : `✗ ${vres.errors.length} error${vres.errors.length === 1 ? "" : "s"}`}
            </span>
          )}
          {showRevalidate && (
            <button
              type="button"
              onClick={() => void onValidate()}
              disabled={validating}
              className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
              title="Re-run the compile-check on this Apex and auto-fix — does not save or deploy"
            >
              {validating
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <ShieldCheck className="h-3.5 w-3.5" />}
              {validating ? "validating…" : "Re-validate"}
            </button>
          )}
          {!hideWorkspaceActions && (replacement || isDelete) && (
            <>
              {!isDelete && (
                <Button
                  type="button"
                  onClick={() =>
                    void openGeneratedCodeInMultiAgentWorkspace({
                      code: shownReplacement,
                      name: actionName,
                      metadataType: actionMetadataType,
                      mode: "workspace",
                      sourceArtifactId: f.source_artifact_id ?? null,
                    })
                  }
                  variant="ghost"
                  size="sm"
                  title="Open generated code in the workspace editor"
                >
                  <Plus className="h-3.5 w-3.5" /> Add to Workspace
                </Button>
              )}
              <Button
                type="button"
                onClick={() =>
                  void openGeneratedCodeInMultiAgentWorkspace({
                    code: shownReplacement,
                    name: actionName,
                    metadataType: actionMetadataType,
                    mode: "compare",
                    original: original || null,
                    sourceArtifactId: f.source_artifact_id ?? null,
                    operation: isDelete ? "deleted" : original ? "modified" : "created",
                  })
                }
                variant="ghost"
                size="sm"
                title={
                  isDelete
                    ? "Review this delete in the workspace editor"
                    : original
                      ? "Compare generated code with current source"
                      : "Compare if this entity exists, otherwise create new"
                }
              >
                {isDelete || original ? <GitCompare className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                {isDelete ? "Review Delete" : original ? "Compare" : "Compare / Create New"}
              </Button>
            </>
          )}
          <Button
            type="button"
            onClick={apply}
            variant="primary"
            size="sm"
            disabled={!diff && !replacement}
            title={applied ? "Copied to clipboard" : "Copy complete replacement or diff"}
          >
            {applied ? "copied" : "Copy"}
          </Button>
        </div>
      </div>
    </div>
  );
});

/** Show the original uploaded image (data-URL/base64). Click enlarges via the
 *  shared artifact layover (the same control the card header exposes). */
function ImageArtifactView({ src, name, onEnlarge }: { src: string; name: string; onEnlarge?: () => void }) {
  const img = <img src={src} alt={name} className="max-h-[420px] w-auto max-w-full rounded object-contain" />;
  return onEnlarge ? (
    <button type="button" onClick={onEnlarge} title="Click to enlarge" className="block focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary)/0.4)]">
      {img}
    </button>
  ) : img;
}

function CodeArtifactView({
  code,
  name,
  language,
  copy,
  copied,
  hideWorkspaceActions,
}: {
  code: string;
  name: string;
  language: string;
  copy: (e: React.MouseEvent) => void;
  copied: boolean;
  hideWorkspaceActions?: boolean;
}) {
  const actionName = extractApexName(code) || bareClassName(name);
  const metadataType = inferMetadataType(name, code, language === "apex" ? "ApexClass" : "ApexClass");
  return (
    <div>
      <div className="mb-1.5 flex flex-wrap items-center justify-end gap-1">
        <button
          type="button"
          onClick={copy}
          className="rounded-md px-2 py-0.5 text-[10px] text-muted-foreground hover:bg-muted hover:text-foreground"
          title="Copy content"
        >
          <Copy className="inline h-3 w-3" /> {copied ? "copied" : "copy"}
        </button>
        {!hideWorkspaceActions && (
          <>
            <Button
              type="button"
              onClick={() =>
                void openGeneratedCodeInMultiAgentWorkspace({
                  code,
                  name: actionName,
                  metadataType,
                  mode: "workspace",
                })
              }
              variant="ghost"
              size="sm"
              title="Open code in the workspace editor"
            >
              <Plus className="h-3.5 w-3.5" /> Add to Workspace
            </Button>
            <Button
              type="button"
              onClick={() =>
                void openGeneratedCodeInMultiAgentWorkspace({
                  code,
                  name: actionName,
                  metadataType,
                  mode: "compare",
                })
              }
              variant="ghost"
              size="sm"
              title="Compare with org entity if it exists, otherwise create as new"
            >
              <GitCompare className="h-3.5 w-3.5" /> Compare / Create New
            </Button>
          </>
        )}
      </div>
      <InlineMonaco value={code} language={language} tone="new" />
    </div>
  );
}

function lineCount(value: string): number {
  if (!value) return 0;
  // Count line breaks (\n, \r, \r\n); lines = breaks + 1.
  let lines = 1;
  for (let i = 0; i < value.length; i++) {
    const c = value[i];
    if (c === "\n") {
      lines++;
    } else if (c === "\r") {
      lines++;
      if (value[i + 1] === "\n") i++; // treat CRLF as one break
    }
  }
  return lines;
}

function languageForPatch(f: CodePatchFix): string {
  const type = (f.metadata_type || "").toLowerCase();
  const file = (f.file || "").toLowerCase();
  if (type.includes("flow") || type.includes("xml") || file.endsWith(".xml")) return "xml";
  if (type.includes("validationrule") || type.includes("customfield")) return "xml";
  if (type.includes("apex") || file.endsWith(".cls") || file.endsWith(".trigger")) return "apex";
  if (file.endsWith(".js")) return "javascript";
  if (file.endsWith(".html")) return "html";
  if (file.endsWith(".css")) return "css";
  return "apex";
}

function InlineMonaco({
  value,
  language,
  tone,
}: {
  value: string;
  language: string;
  tone: "current" | "new";
}) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const monacoRef = useRef<Monaco | null>(null);

  const beforeMount = useCallback((monaco: Monaco) => {
    monacoRef.current = monaco;
    registerAppMonacoThemes(monaco, document.documentElement.classList.contains("dark"));
  }, []);

  useEffect(() => {
    if (monacoRef.current) {
      registerAppMonacoThemes(monacoRef.current, isDark);
    }
  }, [isDark]);

  return (
    <div
      className="overflow-hidden rounded-md border"
      style={{
        borderColor: tone === "current"
          ? "var(--ma-border)"
          : "color-mix(in oklab, hsl(var(--primary)) 45%, var(--ma-border))",
      }}
    >
      <Editor
        height={Math.min(520, Math.max(220, lineCount(value) * 18 + 42))}
        language={language}
        value={value}
        theme={isDark ? "ncoder-dark" : "ncoder-light"}
        beforeMount={beforeMount}
        options={{
          readOnly: true,
          minimap: { enabled: false },
          scrollBeyondLastLine: false,
          wordWrap: "off",
          automaticLayout: true,
          renderLineHighlight: "none",
          fontSize: 12,
          lineNumbersMinChars: 4,
        }}
      />
    </div>
  );
}
